/**
 * Building blocks of the store set-up journey, shared by the two ways of running it:
 *   - register mode  (/signup + /onboarding): account → business → store, then theme → first product
 *   - add-store mode (Add New Store modal):    business → store → theme → first product
 * Validation rules, fields and the store-URL availability check live here once.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Check, X, Loader2, Palette, Package, Rocket, Building2, UserRound } from "lucide-react";
import api from "@/lib/api";
import { INDIA_STATES } from "@/lib/india";
import ImageGallery from "@/components/app/ImageGallery";
import ThemePreviewModal, { ThemeThumb } from "@/components/app/ThemePreview";
import { Field, isBlank, isEmail, isMobile, isGst, toNum } from "@/components/app/FormKit";
import { labelCls } from "@/components/app/ui";

export const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const ACCOUNT_FIELDS = ["name", "email", "mobile", "password"];
export const BUSINESS_FIELDS = ["business_name", "business_category", "business_type", "state", "city", "gst_number"];
export const STORE_FIELDS = ["store_name", "store_slug"];

export const STORE_DEFAULTS = {
  business_name: "", business_category: "", country: "India", state: "", city: "", address: "", gst_number: "",
  business_type: "Sole Proprietor", store_name: "", store_slug: "",
};
export const ACCOUNT_DEFAULTS = { name: "", email: "", mobile: "", password: "" };

export function validateAccount(f, e = {}) {
  if (isBlank(f.name)) e.name = "Full name is required.";
  else if (f.name.trim().length < 2) e.name = "Enter your full name.";
  if (isBlank(f.email)) e.email = "Email is required.";
  else if (!isEmail(f.email)) e.email = "Enter a valid email address.";
  if (isBlank(f.mobile)) e.mobile = "Mobile number is required.";
  else if (!isMobile(f.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
  if (isBlank(f.password)) e.password = "Password is required.";
  else if (f.password.length < 6) e.password = "Password must be at least 6 characters.";
  return e;
}

export function validateBusiness(f, e = {}) {
  if (isBlank(f.business_name)) e.business_name = "Business name is required.";
  if (isBlank(f.business_category)) e.business_category = "Select your business category.";
  if (isBlank(f.business_type)) e.business_type = "Select a business type.";
  if (isBlank(f.state)) e.state = "Select your state.";
  if (isBlank(f.city)) e.city = "City is required.";
  if (!isBlank(f.gst_number) && !isGst(f.gst_number)) e.gst_number = "Enter a valid 15-character GSTIN (e.g. 22AAAAA0000A1Z5).";
  return e;
}

export function validateStoreDetails(f, e = {}) {
  const slug = slugify(f.store_slug || "");
  if (isBlank(f.store_name)) e.store_name = "Store name is required.";
  else if (f.store_name.trim().length < 2) e.store_name = "Store name must be at least 2 characters.";
  if (isBlank(f.store_slug)) e.store_slug = "Store URL is required.";
  else if (slug.length < 3) e.store_slug = "Store URL must be at least 3 characters.";
  else if (slug.length > 30) e.store_slug = "Store URL must be 30 characters or fewer.";
  return e;
}

/** The first product is optional, but if the vendor starts filling it, name and price are both required. */
export function validateProduct(f, e = {}) {
  const started = !isBlank(f.name) || !isBlank(f.price) || f.images.length > 0;
  if (!started) return e;
  if (isBlank(f.name)) e.name = "Add a product name (or clear the form to skip).";
  else if (f.name.trim().length < 2) e.name = "Name must be at least 2 characters.";
  if (isBlank(f.price)) e.price = "Add a price (or clear the form to skip).";
  else if (!(toNum(f.price) > 0)) e.price = "Price must be greater than 0.";
  return e;
}

export function useMeta() {
  const [meta, setMeta] = useState({ categories: [], business_types: [] });
  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  return meta;
}

/** null | "checking" | true | false — debounced availability of a store URL. */
export function useSlugStatus(rawSlug) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!rawSlug) { setStatus(null); return undefined; }
    const slug = slugify(rawSlug);
    if (slug.length < 3) { setStatus(null); return undefined; }
    setStatus("checking");
    const t = setTimeout(() => {
      api.get(`/auth/slug-available/${slug}`).then(({ data }) => setStatus(data.available)).catch(() => setStatus(null));
    }, 400);
    return () => clearTimeout(t);
  }, [rawSlug]);
  return status;
}

/** Payload for the store part of registration / add-store. */
export function storePayload(f) {
  return {
    business_name: f.business_name, business_category: f.business_category, business_type: f.business_type,
    country: f.country, state: f.state, city: f.city, address: f.address,
    gst_number: (f.gst_number || "").trim().toUpperCase(), store_name: f.store_name.trim(), store_slug: slugify(f.store_slug),
  };
}

export function useStepValidator(parts) {
  return useCallback((f) => parts.reduce((e, fn) => fn(f, e), {}), []); // eslint-disable-line react-hooks/exhaustive-deps
}

// ---------------------------------------------------------------- fields

export function AccountFields({ bind, tp = "signup" }) {
  return (
    <>
      <Field className="col-span-2 sm:col-span-1" label="Full name" required t={`${tp}-name`} autoComplete="name" {...bind("name")} />
      <Field className="col-span-2 sm:col-span-1" label="Email" required type="email" t={`${tp}-email`} autoComplete="email" {...bind("email")} />
      <Field className="col-span-2 sm:col-span-1" label="Mobile" required type="tel" prefix="+91" maxLength={10} t={`${tp}-mobile`} placeholder="10-digit number" {...bind("mobile")} />
      <Field className="col-span-2 sm:col-span-1" label="Password" required type="password" t={`${tp}-password`} autoComplete="new-password" hint="At least 6 characters." {...bind("password")} />
    </>
  );
}

export function BusinessFields({ bind, meta, tp = "signup" }) {
  return (
    <>
      <Field className="col-span-2 sm:col-span-1" label="Business name" required t={`${tp}-business-name`} {...bind("business_name")} />
      <Field className="col-span-2 sm:col-span-1" label="Business category" required type="select" options={meta.categories} t={`${tp}-category`} {...bind("business_category")} />
      <Field className="col-span-2 sm:col-span-1" label="Business type" required type="select" options={meta.business_types} t={`${tp}-btype`} {...bind("business_type")} />
      <Field className="col-span-2 sm:col-span-1" label="GST number" maxLength={15} t={`${tp}-gst`} placeholder="Optional" inputClassName="uppercase" {...bind("gst_number")} />
      <Field className="col-span-2 sm:col-span-1" label="State" required type="select" options={INDIA_STATES} t={`${tp}-state`} {...bind("state")} />
      <Field className="col-span-2 sm:col-span-1" label="City" required t={`${tp}-city`} {...bind("city")} />
      <Field className="col-span-2" label="Address" t={`${tp}-address`} placeholder="Optional" {...bind("address")} />
    </>
  );
}

export function StoreFields({ form, slugStatus, tp = "signup" }) {
  const { values: f, set, bind } = form;
  const [slugEdited, setSlugEdited] = useState(false);
  const slugErr = bind("store_slug").error || (slugStatus === false && f.store_slug ? "This store URL is already taken." : undefined);
  return (
    <>
      <Field className="col-span-2" label="Store name" required t={`${tp}-store-name`} maxLength={60} placeholder="e.g. Apex Gear"
        value={f.store_name} onBlur={bind("store_name").onBlur} error={bind("store_name").error}
        onChange={(v) => { set("store_name", v); if (!slugEdited) set("store_slug", slugify(v)); }} />
      <div className="col-span-2">
        <label className={labelCls}>Store URL<span className="text-red-500 ml-0.5">*</span></label>
        <div className={`flex items-center rounded-lg border bg-white transition focus-within:ring-2 overflow-hidden ${slugErr ? "border-red-500 focus-within:ring-red-500/15" : "border-slate-300 focus-within:border-blue-600 focus-within:ring-blue-600/15"}`}>
          <span className="pl-3 text-slate-400 text-[13px] font-mono">sell24x7.com/store/</span>
          <input value={f.store_slug} data-testid={`${tp}-store-slug`} placeholder="my-store" maxLength={40} aria-invalid={slugErr ? "true" : "false"}
            onBlur={bind("store_slug").onBlur} onChange={(e) => { setSlugEdited(e.target.value !== ""); set("store_slug", e.target.value); }}
            className="flex-1 bg-transparent px-1.5 h-[34px] text-[13px] text-neutral-900 outline-none font-mono" />
          <span className="pr-3">
            {slugStatus === "checking" && <Loader2 size={15} className="animate-spin text-slate-400" />}
            {slugStatus === true && <Check size={15} className="text-green-600" />}
            {slugStatus === false && <X size={15} className="text-red-500" />}
          </span>
        </div>
        {slugErr ? <p role="alert" className="mt-1 text-[11px] text-red-600">{slugErr}</p>
          : slugStatus === true ? <p className="mt-1 text-[11px] text-green-600">Available!</p>
          : <p className="mt-1 text-[11px] text-slate-500">3–30 characters: letters, numbers and hyphens. Your store will be live at this address.</p>}
      </div>
    </>
  );
}

/** Themes of the store's category (all themes when none match). `onPick(theme)` is called on click. */
export function ThemePicker({ themes, category, selected, onPick, tp = "onboarding" }) {
  const categoryThemes = themes.filter((t) => t.category === category);
  const visible = categoryThemes.length ? categoryThemes : themes;
  const [viewing, setViewing] = useState(null);
  return (
    <div>
      <h2 className="font-display text-lg font-extrabold text-neutral-900">Choose a theme</h2>
      <p className="text-slate-500 text-[13px] mt-0.5">
        {categoryThemes.length
          ? <>Themes designed for <span className="font-semibold text-orange-600">{category}</span> stores. You can generate a custom AI theme later from your dashboard.</>
          : "You can generate a custom AI theme later from your dashboard."}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {visible.map((t) => (
          <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`${tp}-theme-${t.id}`}
            className={`text-left rounded-lg border-2 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${selected?.id === t.id ? "border-blue-600 shadow-md" : "border-slate-200 hover:border-blue-300"}`}
            style={{ background: t.colors.surface }}>
            <ThemeThumb t={t} height={t.preview_image ? 120 : 56} onPreview={setViewing} />
            <div className="px-3 py-2 flex items-center justify-between" style={{ color: t.colors.text }}>
              <span className="font-semibold text-[13px]">{t.name}</span>
              {selected?.id === t.id && <Check size={15} className="text-blue-600" />}
            </div>
          </button>
        ))}
      </div>
      <ThemePreviewModal theme={viewing} onClose={() => setViewing(null)} onUse={onPick} selected={viewing && selected?.id === viewing.id} />
    </div>
  );
}

export function useThemes() {
  const [themes, setThemes] = useState([]);
  useEffect(() => { api.get("/themes").then(({ data }) => setThemes(data)).catch(() => {}); }, []);
  return themes;
}

export function ProductStep({ form, tp = "onboarding" }) {
  return (
    <div>
      <h2 className="font-display text-lg font-extrabold text-neutral-900">Add your first product</h2>
      <p className="text-slate-500 text-[13px] mt-0.5">Optional — you can skip and add products later.</p>
      <div className="grid lg:grid-cols-2 gap-x-6 gap-y-3 mt-4">
        <div className="space-y-3">
          <Field label="Product name" required={!isBlank(form.values.price) || form.values.images.length > 0} maxLength={120} t={`${tp}-product-name`} placeholder="e.g. Trailblazer Water Bottle" {...form.bind("name")} />
          <Field label="Price" required={!isBlank(form.values.name)} type="number" min="0" step="0.01" prefix="₹" t={`${tp}-product-price`} {...form.bind("price")} />
        </div>
        <div>
          <div className="text-[12px] font-semibold text-slate-700 mb-1">Product images</div>
          <ImageGallery value={form.values.images} onChange={(imgs) => form.set("images", imgs)} max={6} compact />
        </div>
      </div>
    </div>
  );
}

/** Horizontal stepper (numbered when `compact`, bars otherwise). steps: [{label, icon}] */
export function StepProgress({ steps, step }) {
  return (
    <div className="flex items-center justify-center gap-3 sm:gap-4" role="list" aria-label="Progress" data-testid="step-progress">
      {steps.map((s, i) => {
        const n = i + 1;
        const Icon = s.icon;
        return (
          <React.Fragment key={s.label}>
            <div role="listitem" aria-current={step === n ? "step" : undefined} className={`flex items-center gap-2 ${step > n ? "text-green-600" : step === n ? "text-blue-600" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                step > n ? "border-green-500 bg-green-50" : step === n ? "border-blue-600 bg-blue-50 shadow-[0_0_0_4px_rgba(37,99,235,0.12)]" : "border-slate-300 bg-white"}`}>
                {step > n ? <Check size={15} /> : <Icon size={15} />}
              </div>
              <span className="text-[13px] font-semibold hidden sm:block">{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`w-8 sm:w-12 h-0.5 rounded transition-colors ${step > n ? "bg-green-500" : "bg-slate-200"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export const STEP_ICONS = { account: UserRound, business: Building2, store: Rocket, theme: Palette, product: Package };
